import { computed, inject, provide, ref, watchEffect, onBeforeUnmount, type ComputedRef } from 'vue'
import { useRoute, useRouter, type RouteLocationNormalized } from 'vue-router'

export interface BreadcrumbItem {
  label: string
  path?: string
  disabled?: boolean
  icon?: string
}

/**
 * Breadcrumb configuration for a route
 * Can be extended in route meta for custom breadcrumbs
 */
export interface BreadcrumbMeta {
  /** Custom label for breadcrumb (defaults to route.meta.title) */
  breadcrumbLabel?: string
  /** Custom icon for breadcrumb */
  breadcrumbIcon?: string
  /** Parent route name for hierarchical breadcrumbs */
  breadcrumbParent?: string
  /** Whether to hide this route from breadcrumbs */
  hideFromBreadcrumb?: boolean
  /** Custom function to generate breadcrumb label dynamically */
  breadcrumbLabelFn?: (route: RouteLocationNormalized) => string
}

/**
 * A detail page can name itself (an invoice number, a package name) so the last
 * breadcrumb shows that instead of a generic "Invoice". Kept per path, so a title
 * never sticks to the next page.
 */
const pageTitle = ref<{ path: string; label: string } | null>(null)

export function useBreadcrumbTitle(source: () => string | null | undefined): void {
  const route = useRoute()
  watchEffect(() => {
    const label = source()?.trim()
    if (label) pageTitle.value = { path: route.path, label }
  })
  onBeforeUnmount(() => {
    pageTitle.value = null
  })
}

/**
 * Composable for generating breadcrumbs from Vue Router routes
 * Provides reactive breadcrumbs based on current route
 */
export function useBreadcrumbs(): {
  breadcrumbs: ComputedRef<BreadcrumbItem[]>
  generateBreadcrumbs: (route?: RouteLocationNormalized) => BreadcrumbItem[]
} {
  const route = useRoute()
  const router = useRouter()

  /**
   * Route name to breadcrumb label mapping
   * Override route meta.title or provide custom labels
   */
  const routeBreadcrumbMap: Record<string, string | ((route: RouteLocationNormalized) => string)> = {
    // Public routes
    login: 'Login',
    signup: 'Sign Up',
    'forgot-password': 'Forgot Password',
    'reset-password': 'Reset Password',

    // Main routes
    home: 'Dashboard',
    packages: 'Packages',
    'package-details': (route) => `Package ${route.params.id}`,
    visa: 'Visa',
    'visa-details': (route) => `Visa ${route.params.id}`,
    transports: 'Transports',
    invoices: 'Invoices',
    'invoice-details': (route) => `${route.params.type} Invoice #${route.params.id}`,
    'booking-vouchers': 'Booking Vouchers',
    'booking-voucher-details': (route) => `Booking Voucher #${route.params.id}`,
    'hotel-vouchers': 'Hotel Vouchers',
    'hotel-voucher-details': (route) => `Hotel Voucher #${route.params.id}`,
    'package-requests': 'Package Requests',
    'package-request-create': 'Create Package Request',
    'package-request-details': (route) => `Request #${route.params.id}`,

    // Finance routes
    finance: 'Finance',
    'finance-ledger': 'Company Ledger',
    'finance-payments': 'Payment History',
    'finance-refunds': 'Refund History',
    'finance-refund-receipt': (route) => `Refund Receipt #${route.params.id}`,
    'finance-balance-requests': 'Balance Requests',
    'finance-balance-request-create': 'Submit Balance Request',
    'finance-balance-receipt': (route) => `Balance Receipt #${route.params.id}`,

    // Passport routes
    passports: 'Visa Status',
    'passport-details': (route) => `Passport #${route.params.id}`,

    // Company routes
    'company-profile': 'Company Profile',
    'company-edit': 'Edit Company',

    // User management routes
    users: 'Users',
    'user-create': 'Create User',
    'user-edit': (route) => `Edit User #${route.params.id}`,

    // Other routes
    'change-password': 'Change Password',
    notifications: 'Notifications',
    'notification-settings': 'Notification Settings',
    chat: 'Chat',
    search: 'Search',
    'not-found': 'Page Not Found',
  }

  /**
   * Route hierarchy mapping for parent-child relationships
   * Key: child route name, Value: parent route name
   */
  const routeHierarchy: Record<string, string> = {
    'package-details': 'packages',
    'visa-details': 'visa',
    'invoice-details': 'invoices',
    'booking-voucher-details': 'booking-vouchers',
    'hotel-voucher-details': 'hotel-vouchers',
    'package-request-create': 'package-requests',
    'package-request-details': 'package-requests',
    'finance-ledger': 'finance',
    'finance-payments': 'finance',
    'finance-refunds': 'finance',
    'finance-refund-receipt': 'finance-refunds',
    'finance-balance-requests': 'finance',
    'finance-balance-request-create': 'finance-balance-requests',
    'finance-balance-receipt': 'finance-balance-requests',
    'passport-details': 'passports',
    'company-edit': 'company-profile',
    'user-create': 'users',
    'user-edit': 'users',
  }

  /**
   * Route name to path mapping for breadcrumb links
   * Falls back to router.resolve if not found
   */
  const routePathMap: Record<string, string | ((route: RouteLocationNormalized) => string)> = {
    home: '/home',
    packages: '/packages',
    'package-details': (route) => `/package/${route.params.id}`,
    visa: '/visa',
    'visa-details': (route) => `/visa/${route.params.id}`,
    transports: '/transports',
    invoices: '/invoices',
    'invoice-details': (route) => `/invoices/${route.params.type}/${route.params.id}`,
    'booking-vouchers': '/booking-vouchers',
    'booking-voucher-details': (route) => `/booking-vouchers/${route.params.id}`,
    'hotel-vouchers': '/hotel-vouchers',
    'hotel-voucher-details': (route) => `/hotel-vouchers/${route.params.id}`,
    'package-requests': '/package-requests',
    'package-request-create': '/package-requests/create',
    'package-request-details': (route) => `/package-requests/${route.params.id}`,
    finance: '/finance',
    'finance-ledger': '/finance/ledger',
    'finance-payments': '/finance/payments',
    'finance-refunds': '/finance/refunds',
    'finance-refund-receipt': (route) => `/finance/refunds/${route.params.id}/receipt`,
    'finance-balance-requests': '/finance/balance-requests',
    'finance-balance-request-create': '/finance/balance-requests/create',
    'finance-balance-receipt': (route) => `/finance/balance-requests/${route.params.id}/receipt`,
    passports: '/passports',
    'passport-details': (route) => `/passports/${route.params.id}`,
    'company-profile': '/company/profile',
    'company-edit': '/company/edit',
    users: '/users',
    'user-create': '/users/create',
    'user-edit': (route) => `/users/${route.params.id}/edit`,
    'change-password': '/change-password',
    notifications: '/notifications',
    'notification-settings': '/notification-settings',
    chat: '/chat',
    search: '/search',
  }

  /**
   * Get breadcrumb label for a route
   */
  function getBreadcrumbLabel(route: RouteLocationNormalized): string {
    const routeName = route.name as string | undefined

    // Handle undefined route name
    if (!routeName) {
      return 'Unknown'
    }

    // Check route meta for custom breadcrumb label
    const meta = route.meta as Record<string, unknown> & BreadcrumbMeta
    if (meta.breadcrumbLabel) {
      return meta.breadcrumbLabel
    }
    if (meta.breadcrumbLabelFn) {
      return meta.breadcrumbLabelFn(route)
    }

    // Check route meta title
    if (meta.title) {
      return meta.title as string
    }

    // Check route breadcrumb map
    const label = routeBreadcrumbMap[routeName]
    if (typeof label === 'function') {
      return label(route)
    }
    if (label) {
      return label
    }

    // Fallback: generate from route name
    return routeName
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  /**
   * Get breadcrumb icon for a route
   */
  function getBreadcrumbIcon(route: RouteLocationNormalized): string | undefined {
    const routeName = route.name as string | undefined
    if (!routeName) {
      return undefined
    }

    const meta = route.meta as Record<string, unknown> & BreadcrumbMeta
    if (meta.breadcrumbIcon) {
      return meta.breadcrumbIcon
    }

    const iconMap: Record<string, string> = {
      home: 'fa fa-tachometer-alt',
      packages: 'fa fa-suitcase',
      visa: 'fa fa-id-card',
      transports: 'fa fa-bus',
      invoices: 'fa fa-file-invoice',
      'booking-vouchers': 'fa fa-ticket-alt',
      'hotel-vouchers': 'fa fa-hotel',
      'package-requests': 'fa fa-envelope-open',
      finance: 'fa fa-chart-line',
      passports: 'fa fa-passport',
      'company-profile': 'fa fa-building',
      users: 'fa fa-users',
      notifications: 'fa fa-bell',
      chat: 'fa fa-comments',
      search: 'fa fa-search',
      'change-password': 'fa fa-key',
    }

    return iconMap[routeName]
  }

  /**
   * Get breadcrumb path for a route
   */
  function getBreadcrumbPath(route: RouteLocationNormalized): string | undefined {
    const routeName = route.name as string

    // Check if route should be hidden from breadcrumbs
    const meta = route.meta as Record<string, unknown> & BreadcrumbMeta
    if (meta.hideFromBreadcrumb) {
      return undefined
    }

    // Check if route has a custom path resolver
    const pathResolver = routePathMap[routeName]
    if (typeof pathResolver === 'function') {
      return pathResolver(route)
    }
    if (pathResolver) {
      return pathResolver
    }

    // Try to resolve via router
    try {
      const resolved = router.resolve({ name: routeName, params: route.params, query: route.query })
      return resolved.href
    } catch {
      // Route might not be found or might require params we don't have
      return undefined
    }
  }

  /**
   * Build breadcrumb hierarchy for a route
   */
  function buildBreadcrumbHierarchy(route: RouteLocationNormalized): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = []

    // Handle undefined route name
    if (!route.name) {
      return breadcrumbs
    }

    // Build hierarchy chain from current route up to root
    const hierarchy: RouteLocationNormalized[] = []
    let currentRoute: RouteLocationNormalized | null = route

    // Add current route and its parents
    while (currentRoute) {
      const meta = currentRoute.meta as Record<string, unknown> & BreadcrumbMeta
      if (!meta.hideFromBreadcrumb && currentRoute.name) {
        hierarchy.unshift(currentRoute)
      }

      const currentName = currentRoute.name as string
      const parentName = meta.breadcrumbParent || routeHierarchy[currentName]

      if (parentName) {
        // Try to find parent route in router
        const parentRoute = router.getRoutes().find((r) => r.name === parentName)
        if (parentRoute) {
          // Create a mock route location for parent
          currentRoute = {
            ...currentRoute,
            name: parentName,
            path: router.resolve({ name: parentName }).href,
            params: {},
            query: {},
            meta: parentRoute.meta,
          } as RouteLocationNormalized
        } else {
          currentRoute = null
        }
      } else {
        currentRoute = null
      }
    }

    // Build breadcrumb items from hierarchy
    hierarchy.forEach((r, index) => {
      const path = getBreadcrumbPath(r)
      const isLast = index === hierarchy.length - 1

      const ownTitle = isLast && pageTitle.value?.path === route.path ? pageTitle.value.label : ''
      breadcrumbs.push({
        label: ownTitle || getBreadcrumbLabel(r),
        path: path && !isLast ? path : undefined,
        disabled: isLast,
        icon: getBreadcrumbIcon(r),
      })
    })

    return breadcrumbs
  }

  /**
   * Main computed breadcrumbs based on current route
   */
  const breadcrumbs = computed<BreadcrumbItem[]>(() => {
    // Skip breadcrumbs for auth pages
    const guestOnlyRoutes = ['login', 'signup', 'forgot-password', 'reset-password', 'not-found']
    if (!route.name || guestOnlyRoutes.includes(route.name as string)) {
      return []
    }

    return buildBreadcrumbHierarchy(route)
  })

  /**
   * Generate breadcrumbs for a specific route (useful for testing or pre-generation)
   */
  function generateBreadcrumbs(targetRoute?: RouteLocationNormalized): BreadcrumbItem[] {
    const target = targetRoute || route
    const guestOnlyRoutes = ['login', 'signup', 'forgot-password', 'reset-password', 'not-found']
    if (!target.name || guestOnlyRoutes.includes(target.name as string)) {
      return []
    }
    return buildBreadcrumbHierarchy(target)
  }

  return {
    breadcrumbs,
    generateBreadcrumbs,
  }
}

/**
 * Global breadcrumb state for cross-component access
 * Use provide/inject for app-wide breadcrumb access
 */
export const breadcrumbsKey = Symbol('breadcrumbs')

export function provideBreadcrumbs() {
  const { breadcrumbs, generateBreadcrumbs } = useBreadcrumbs()
  provide(breadcrumbsKey, { breadcrumbs, generateBreadcrumbs })
}

export function useBreadcrumbsState(): {
  breadcrumbs: ComputedRef<BreadcrumbItem[]>
  generateBreadcrumbs: (route?: RouteLocationNormalized) => BreadcrumbItem[]
} {
  const injected = inject<{
    breadcrumbs: ComputedRef<BreadcrumbItem[]>
    generateBreadcrumbs: (route?: RouteLocationNormalized) => BreadcrumbItem[]
  }>(breadcrumbsKey)

  if (!injected) {
    return useBreadcrumbs()
  }

  return injected
}